<template>
    <div>
        <!-- Step 1: Transfer Info -->
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-5">
            <div
                class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-500 tw-text-white tw-text-sm tw-font-bold tw-flex-shrink-0 tw-shadow-sm"
            >
                1
            </div>
            <span
                class="tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider"
                >{{ $t("cashTransfer.transferInfo") }}</span
            >
            <div class="tw-flex-1 tw-border-t-2 tw-border-blue-100"></div>
        </div>

        <!-- Requester + Date -->
        <div
            class="tw-bg-blue-50 tw-rounded-xl tw-border tw-border-blue-100 tw-p-4 tw-mb-5"
        >
            <div class="tw-grid tw-grid-cols-2 tw-gap-4">
                <div>
                    <div
                        class="tw-text-xs tw-font-semibold tw-text-blue-500 tw-uppercase tw-tracking-wide tw-mb-1.5"
                    >
                        {{ $t("cashTransfer.transferBy") }}
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2.5">
                        <span
                            class="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-500 tw-text-white tw-text-sm tw-font-bold tw-flex-shrink-0 tw-shadow-sm"
                        >
                            {{ nameInitial }}
                        </span>
                        <span
                            class="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate"
                            >{{ transferByName }}</span
                        >
                    </div>
                </div>
                <div>
                    <div
                        class="tw-text-xs tw-font-semibold tw-text-blue-500 tw-uppercase tw-tracking-wide tw-mb-1.5"
                    >
                        {{ $t("cashTransfer.transferDate") }}
                        <span class="tw-text-red-400">*</span>
                    </div>
                    <DatePicker
                        v-model="model.transfer_date"
                        type="date"
                        format="dd-MM-yyyy"
                        :placeholder="$t('cashTransfer.transferDate')"
                        style="width: 100%"
                        :class="{ 'is-invalid': validate.has('transfer_date') }"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="validate.has('transfer_date')"
                    >
                        {{ validate.first("transfer_date") }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Amount -->
        <div class="tw-mb-5">
            <label
                class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-flex tw-items-center tw-gap-1"
            >
                {{ $t("cashTransfer.amount") }}
                <span class="tw-text-red-400">*</span>
            </label>
            <Input
                v-model="model.transfer_amount"
                type="number"
                :min="0"
                :placeholder="$t('cashTransfer.amount')"
                :class="{ 'is-invalid': validate.has('transfer_amount') }"
                size="large"
            >
                <Select
                    slot="append"
                    v-model="model.currency_id"
                    style="width: 130px"
                >
                    <Option
                        v-for="cur in currencies"
                        :key="cur.currency_id"
                        :value="cur.currency_id"
                    >
                        {{ cur.currency_code }} — {{ cur.currency_name_en }}
                    </Option>
                </Select>
            </Input>
            <div
                class="invalid-feedback"
                v-if="validate.has('transfer_amount')"
            >
                {{ validate.first("transfer_amount") }}
            </div>
            <transition name="fade">
                <div
                    v-if="formattedAmount"
                    class="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-bg-blue-50 tw-border tw-border-blue-200 tw-text-blue-700 tw-text-sm tw-font-semibold"
                >
                    <i
                        class="fas fa-check-circle tw-text-xs tw-text-blue-400"
                    ></i>
                    {{ formattedAmount }}
                </div>
            </transition>
        </div>

        <!-- Remark -->
        <div class="tw-mb-6">
            <label class="tw-block tw-text-xs tw-text-gray-500 tw-mb-1">{{
                $t("cashTransferReport.remark")
            }}</label>
            <Select
                v-model="model.remark"
                clearable
                filterable
                style="width: 100%"
                :placeholder="$t('all')"
            >
                <Option v-for="r in remarkOptions" :key="r" :value="r">
                    {{ r }}
                </Option>
            </Select>
        </div>

        <!-- Step 2: Destination Account -->
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-5">
            <div
                class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-green-500 tw-text-white tw-text-sm tw-font-bold tw-flex-shrink-0 tw-shadow-sm"
            >
                2
            </div>
            <div class="tw-flex-1">
                <div class="tw-flex tw-items-center tw-gap-3">
                    <span
                        class="tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider"
                        >{{ $t("cashTransfer.destinationBankAccount") }}</span
                    >
                    <div
                        class="tw-flex-1 tw-border-t-2 tw-border-green-100"
                    ></div>
                </div>
                <p class="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">
                    {{ $t("cashTransfer.destinationBankAccountHint") }}
                </p>
            </div>
        </div>

        <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
            <div>
                <label
                    class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-flex tw-items-center tw-gap-1"
                >
                    {{ $t("cashTransfer.bank") }}
                    <span class="tw-text-red-400">*</span>
                </label>
                <Select
                    v-model="model.bank_id"
                    :placeholder="$t('cashTransfer.bank')"
                    filterable
                    :class="{ 'is-invalid': validate.has('bank_id') }"
                    @on-change="onBankChange"
                >
                    <Option
                        v-for="bank in banks"
                        :key="bank.bank_id"
                        :value="bank.bank_id"
                        :label="bank.bank_name"
                    >
                        <span class="tw-flex tw-items-center tw-gap-2">
                            <span
                                class="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-bold"
                            >
                                {{ bank.bank_name.charAt(0).toUpperCase() }}
                            </span>
                            {{ bank.bank_name }}
                        </span>
                    </Option>
                </Select>
                <div class="invalid-feedback" v-if="validate.has('bank_id')">
                    {{ validate.first("bank_id") }}
                </div>
            </div>

            <div>
                <label
                    class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-flex tw-items-center tw-gap-1"
                >
                    {{ $t("cashTransfer.bankAccount") }}
                    <span class="tw-text-red-400">*</span>
                </label>
                <Select
                    v-model="selectedAccountId"
                    :placeholder="
                        model.bank_id
                            ? $t('cashTransfer.selectAccount')
                            : $t('cashTransfer.selectBankFirst')
                    "
                    filterable
                    :disabled="!model.bank_id || filteredAccounts.length === 0"
                    :class="{ 'is-invalid': validate.has('bank_account_no') }"
                    @on-change="onAccountChange"
                >
                    <Option
                        v-for="acc in filteredAccounts"
                        :key="acc.account_id"
                        :value="acc.account_id"
                        :label="acc.account_no"
                    >
                        <div class="tw-py-0.5">
                            <div
                                class="tw-font-medium tw-text-gray-800 tw-text-sm"
                            >
                                {{ acc.account_no }}
                            </div>
                            <div class="tw-text-xs tw-text-gray-400">
                                {{ acc.account_name }}
                            </div>
                        </div>
                    </Option>
                </Select>
                <div
                    class="invalid-feedback"
                    v-if="validate.has('bank_account_no')"
                >
                    {{ validate.first("bank_account_no") }}
                </div>
                <div
                    v-if="model.bank_id && filteredAccounts.length === 0"
                    class="tw-mt-1 tw-text-xs tw-text-amber-600 tw-flex tw-items-center tw-gap-1"
                >
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ $t("cashTransfer.noAccountsForBank") }}
                </div>
            </div>
        </div>

        <!-- Account card -->
        <div class="tw-mb-6">
            <transition name="slide-down">
                <div
                    v-if="selectedAccount"
                    class="tw-rounded-xl tw-border tw-border-green-200 tw-bg-white tw-overflow-hidden tw-shadow-sm"
                >
                    <div
                        class="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-green-500"
                    >
                        <i
                            class="fas fa-university tw-text-white tw-text-xs"
                        ></i>
                        <span
                            class="tw-text-xs tw-font-bold tw-text-white tw-uppercase tw-tracking-wider"
                            >{{ $t("cashTransfer.selectedAccount") }}</span
                        >
                        <div class="tw-flex-1"></div>
                        <button
                            type="button"
                            class="tw-text-green-200 hover:tw-text-white tw-transition-colors tw-text-xs"
                            @click="clearAccount"
                            :title="$t('cashTransfer.clearAccount')"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="tw-flex tw-items-stretch">
                        <div class="tw-flex-1 tw-p-4">
                            <div
                                class="tw-flex tw-items-center tw-gap-2 tw-mb-3"
                            >
                                <span
                                    class="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full tw-bg-green-100 tw-text-green-700 tw-font-bold tw-text-base"
                                >
                                    {{
                                        selectedBank
                                            ? selectedBank.bank_name
                                                  .charAt(0)
                                                  .toUpperCase()
                                            : "?"
                                    }}
                                </span>
                                <span class="tw-font-bold tw-text-gray-800">{{
                                    selectedBank ? selectedBank.bank_name : ""
                                }}</span>
                            </div>
                            <div class="tw-grid tw-grid-cols-2 tw-gap-3">
                                <div
                                    class="tw-bg-gray-50 tw-rounded-lg tw-p-2.5"
                                >
                                    <div
                                        class="tw-text-xs tw-text-gray-400 tw-mb-0.5"
                                    >
                                        {{ $t("cashTransfer.bankAccountNo") }}
                                    </div>
                                    <div
                                        class="tw-font-mono tw-font-bold tw-text-gray-900 tw-text-sm"
                                    >
                                        {{ selectedAccount.account_no }}
                                    </div>
                                </div>
                                <div
                                    class="tw-bg-gray-50 tw-rounded-lg tw-p-2.5"
                                >
                                    <div
                                        class="tw-text-xs tw-text-gray-400 tw-mb-0.5"
                                    >
                                        {{ $t("cashTransfer.bankAccountName") }}
                                    </div>
                                    <div
                                        class="tw-font-semibold tw-text-gray-900 tw-text-sm"
                                    >
                                        {{ selectedAccount.account_name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="selectedAccount.qr_image"
                            class="tw-flex-shrink-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-p-4 tw-bg-green-50 tw-border-l tw-border-green-100 tw-w-48"
                        >
                            <img
                                :src="selectedAccount.qr_image"
                                class="tw-w-36 tw-h-36 tw-object-contain tw-rounded-lg tw-border-2 tw-border-green-200 tw-bg-white tw-p-1 tw-shadow-sm tw-cursor-zoom-in hover:tw-border-green-400 tw-transition-colors"
                                :alt="$t('bankProfile.qrImage')"
                                @click="showQr = true"
                            />
                            <span
                                class="tw-text-xs tw-text-green-600 tw-font-medium tw-flex tw-items-center tw-gap-1"
                            >
                                <i class="fas fa-qrcode"></i>
                                {{ $t("cashTransfer.scanToTransfer") }}
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    v-else-if="model.bank_id && filteredAccounts.length > 0"
                    class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-rounded-xl tw-border tw-border-dashed tw-border-green-300 tw-bg-green-50 tw-text-sm tw-text-green-500"
                >
                    <i class="fas fa-hand-point-up tw-text-xs"></i>
                    <span>{{ $t("cashTransfer.selectAccountHint") }}</span>
                </div>
            </transition>
        </div>

        <!-- QR Lightbox -->
        <Modal
            v-model="showQr"
            :footer-hide="true"
            width="420"
            :title="selectedBank ? selectedBank.bank_name : ''"
            :styles="{ top: '50px' }"
        >
            <div
                class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-3"
                v-if="selectedAccount"
            >
                <img
                    :src="selectedAccount.qr_image"
                    style="max-width: 480px; max-height: 480px; width: auto; height: auto;"
                    class="tw-object-contain tw-rounded-xl tw-border-2 tw-border-gray-200 tw-bg-white tw-p-3 tw-shadow"
                />
                <div class="tw-text-center">
                    <div
                        class="tw-font-mono tw-font-bold tw-text-gray-800 tw-text-lg"
                    >
                        {{ selectedAccount.account_no }}
                    </div>
                    <div class="tw-text-sm tw-text-gray-500">
                        {{ selectedAccount.account_name }}
                    </div>
                </div>
            </div>
        </Modal>

        <!-- Step 3: Bank Slip -->
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-5">
            <div
                class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-purple-500 tw-text-white tw-text-sm tw-font-bold tw-flex-shrink-0 tw-shadow-sm"
            >
                3
            </div>
            <span
                class="tw-text-sm tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider"
                >{{ $t("cashTransfer.bankSlip") }}</span
            >
            <div class="tw-flex-1 tw-border-t-2 tw-border-purple-100"></div>
        </div>

        <div
            class="tw-bg-purple-50 tw-rounded-xl tw-border tw-border-purple-100 tw-p-4"
        >
            <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4">
                <div>
                    <label
                        class="tw-text-xs tw-font-semibold tw-text-purple-600 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-flex tw-items-center tw-gap-1"
                    >
                        {{ $t("cashTransfer.bankSlipPhoto") }}
                        <span class="tw-text-red-400">*</span>
                    </label>
                    <bank-slip-photo-upload
                        :value="model.bank_slip_photo"
                        :loading="uploadingPhoto"
                        @change="onPhotoChange"
                        @clear="model.bank_slip_photo = null"
                    />
                    <div
                        class="invalid-feedback d-block"
                        v-if="validate.has('bank_slip_photo')"
                    >
                        {{ validate.first("bank_slip_photo") }}
                    </div>
                </div>
                <div>
                    <label
                        class="tw-text-xs tw-font-semibold tw-text-purple-600 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-flex tw-items-center tw-gap-1"
                    >
                        {{ $t("cashTransfer.bankSlipDate") }}
                        <span class="tw-text-red-400">*</span>
                    </label>
                    <DatePicker
                        :value="model.bank_slip_date"
                        type="date"
                        format="dd-MM-yyyy"
                        style="width: 100%"
                        :class="{
                            'is-invalid': validate.has('bank_slip_date')
                        }"
                        @on-change="model.bank_slip_date = $event"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="validate.has('bank_slip_date')"
                    >
                        {{ validate.first("bank_slip_date") }}
                    </div>
                </div>
            </div>
            <div>
                <label
                    class="tw-text-xs tw-font-semibold tw-text-purple-600 tw-uppercase tw-tracking-wide tw-mb-1.5 tw-block"
                    >{{ $t("cashTransfer.bankSlipRemark") }}</label
                >
                <textarea
                    v-model.trim="model.bank_slip_remark"
                    rows="2"
                    class="form-control"
                    :placeholder="$t('cashTransfer.bankSlipRemark')"
                ></textarea>
            </div>
        </div>
    </div>
</template>

<script>
import BankSlipPhotoUpload from "../../components/BankSlipPhotoUpload.vue";

export default {
    name: "CashTransferFormInput",
    components: { BankSlipPhotoUpload },
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        transferByName: { type: String, default: "" },
        currencies: { type: Array, default: () => [] },
        banks: { type: Array, default: () => [] },
        remarkOptions: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedAccountId: null,
            uploadingPhoto: false,
            showQr: false
        };
    },
    watch: {
        banks: {
            immediate: true,
            handler(banks) {
                if (!banks || !banks.length) return;
                if (!this.model.bank_id && banks.length === 1) {
                    this.model.bank_id = banks[0].bank_id;
                }
                if (
                    this.model.bank_id &&
                    this.model.bank_account_no &&
                    !this.selectedAccountId
                ) {
                    const bank = banks.find(
                        b => b.bank_id === this.model.bank_id
                    );
                    if (bank) {
                        const acc = (bank.accounts || []).find(
                            a => a.account_no === this.model.bank_account_no
                        );
                        if (acc) this.selectedAccountId = acc.account_id;
                    }
                }
            }
        },
        filteredAccounts(accounts) {
            if (
                !this.selectedAccountId &&
                this.model.bank_account_no &&
                accounts.length
            ) {
                const acc = accounts.find(
                    a => a.account_no === this.model.bank_account_no
                );
                if (acc) this.selectedAccountId = acc.account_id;
                return;
            }
            if (!this.selectedAccountId && accounts.length === 1) {
                this.selectedAccountId = accounts[0].account_id;
                this.onAccountChange(accounts[0].account_id);
            }
        }
    },
    computed: {
        model() {
            return this.value;
        },
        nameInitial() {
            return this.transferByName
                ? this.transferByName.charAt(0).toUpperCase()
                : "?";
        },
        filteredAccounts() {
            if (!this.model.bank_id) return [];
            const bank = this.banks.find(b => b.bank_id === this.model.bank_id);
            return bank ? bank.accounts || [] : [];
        },
        selectedBank() {
            if (!this.model.bank_id) return null;
            return (
                this.banks.find(b => b.bank_id === this.model.bank_id) || null
            );
        },
        selectedAccount() {
            if (!this.selectedAccountId) return null;
            return (
                this.filteredAccounts.find(
                    a => a.account_id === this.selectedAccountId
                ) || null
            );
        },
        formattedAmount() {
            const amount = parseFloat(this.model.transfer_amount);
            if (!this.model.transfer_amount || isNaN(amount) || amount <= 0)
                return null;
            const cur = this.currencies.find(
                c => c.currency_id === this.model.currency_id
            );
            const code = cur ? cur.currency_code : "";
            return `${code} ${amount.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }
    },
    methods: {
        onBankChange() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        },
        onAccountChange(accountId) {
            const acc = this.filteredAccounts.find(
                a => a.account_id === accountId
            );
            if (acc) {
                this.model.bank_account_no = acc.account_no;
                this.model.bank_account_name = acc.account_name;
            }
        },
        clearAccount() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        },
        onPhotoChange(file) {
            const formData = new FormData();
            formData.append("photo", file);
            this.uploadingPhoto = true;
            this.$store
                .dispatch("finance/cashTransfer/photoUpload", formData)
                .then(path => {
                    this.model.bank_slip_photo = path;
                })
                .finally(() => {
                    this.uploadingPhoto = false;
                });
        }
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-down-enter-active {
    transition: all 0.25s ease;
}
.slide-down-leave-active {
    transition: all 0.2s ease;
}
.slide-down-enter {
    transform: translateY(-8px);
    opacity: 0;
}
.slide-down-leave-to {
    transform: translateY(-4px);
    opacity: 0;
}
</style>

<style></style>
